/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation, type MutationHookOptions } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import React from 'react';
import { Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import {
  DedicatedProjectCreateDocument,
  type DedicatedProjectCreateMutation,
  type DedicatedProjectCreateMutationVariables,
} from '@fai/friendli-suite/generated/client/graphql';
import { successProps, errorProps } from '@fai/ui/components/notification/props';

export const useCreateProject = (
  mutationOptions?: MutationHookOptions<
    DedicatedProjectCreateMutation,
    DedicatedProjectCreateMutationVariables
  >
) => {
  const navigate = useNavigate();
  const { teamId = '' } = useParams();

  const [createProjectMutation, { loading }] = useMutation(DedicatedProjectCreateDocument, {
    update: (cache) => {
      cache.evict({
        id: cache.identify({
          __typename: 'DedicatedSubplan',
          id: teamId,
        }),
        fieldName: 'projects',
      });
      cache.gc();
    },
  });

  const createProject = ({ name, memberIds }: { name: string; memberIds: string[] }) => {
    createProjectMutation({
      variables: { input: { teamId, name, memberIds } },
      onCompleted: (d) => {
        switch (d.dedicatedProjectCreate.__typename) {
          case 'DedicatedProjectCreateSuccess': {
            notifications.show({
              ...successProps,
              message: (
                <Trans
                  i18nKey="dedicated_endpoints.project.project_has_been_created"
                  components={[<b />]}
                  values={{ projectName: name }}
                />
              ),
            });

            navigate(
              Paths.team(teamId).dedicatedEndpoints.project.detail(
                d.dedicatedProjectCreate.project.id
              ).default
            );

            break;
          }
          case 'ProjectNameDuplicateError': {
            notifications.show({
              ...errorProps,
              message: (
                <Trans
                  i18nKey="dedicated_endpoints.project.project_name_duplicated_error_message"
                  components={[<b />]}
                  values={{ projectName: name }}
                />
              ),
            });

            break;
          }

          case 'TeamNotExistError':
          case 'UserNotExistError':
          case 'UserPermissionError': {
            notifications.show({
              ...errorProps,
              message: d.dedicatedProjectCreate.message,
            });

            break;
          }
        }

        if (mutationOptions?.onCompleted) {
          mutationOptions.onCompleted(d);
        }
      },
    });
  };

  return { createProject, loading };
};
